import { useAuth } from '@/app/AuthContext';
import { useTheme, type ThemeMode } from '@/theme/ThemeProvider';
import { PageHeader } from '@/components/PageHeader';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Forbidden } from '@/components/PermissionGate';
import { UsersSection } from '@/features/settings/UsersSection';
import { IntegrationsSection } from '@/features/settings/IntegrationsSection';
import { TelegramSection } from '@/features/settings/TelegramSection';
import { SecuritySection } from '@/features/settings/SecuritySection';
import { AdministrationSection } from '@/features/settings/AdministrationSection';
import { BackgroundSection } from '@/features/settings/BackgroundSection';
import { Permission, roleTitle } from '@/types/permission';
import { v } from '@/theme/tokens';

const THEME_LABELS: Record<ThemeMode, string> = {
  light: 'Светлая',
  dark: 'Тёмная',
  system: 'Как в системе',
};

/** Profile and appearance — available to every signed-in user. */
function ProfileSection() {
  const { user } = useAuth();
  const { mode, setMode, resolved } = useTheme();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 16 }}>
      <Card>
        <SectionTitle right={<Badge tone="accent">{resolved === 'dark' ? 'Тёмная' : 'Светлая'}</Badge>}>
          Внешний вид
        </SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(Object.keys(THEME_LABELS) as ThemeMode[]).map((option) => (
            <label
              key={option}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 10,
                border: `1px solid ${mode === option ? v('accent') : v('line')}`,
                background: mode === option ? v('accent-soft') : 'transparent',
                cursor: 'pointer',
                fontSize: 13.5,
                fontWeight: mode === option ? 600 : 500,
              }}
            >
              <input
                type="radio"
                name="theme"
                checked={mode === option}
                onChange={() => setMode(option)}
                style={{ accentColor: v('accent') }}
              />
              {THEME_LABELS[option]}
            </label>
          ))}
        </div>
        <p style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 0 }}>
          Выбор сохраняется на устройстве. «Как в системе» следует настройкам ОС в реальном времени.
        </p>
      </Card>

      <Card>
        <SectionTitle>Профиль</SectionTitle>
        {[
          ['Имя', user?.full_name],
          ['Email', user?.email],
          ['Роль', roleTitle(user?.role.code)],
        ].map(([label, value]) => (
          <div
            key={label as string}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '9px 0',
              borderBottom: `1px solid ${v('line-soft')}`,
              fontSize: 13.5,
            }}
          >
            <span style={{ color: v('ink-sub') }}>{label as string}</span>
            <span style={{ fontWeight: 600 }}>{(value as string) ?? '—'}</span>
          </div>
        ))}
        <div style={{ fontSize: 12, color: v('ink-muted'), marginTop: 12 }}>
          Права: {user?.permissions.length ?? 0}. Проверка выполняется на сервере при каждом запросе.
        </div>
      </Card>
    </div>
  );
}

/**
 * System settings belong to the owner alone. The navigation item is hidden for
 * everyone else, and reaching /settings directly lands on the refusal below —
 * while the API refuses the underlying requests independently.
 */
export function SettingsPage() {
  const { can } = useAuth();

  if (!can(Permission.MANAGE_SETTINGS)) {
    return (
      <div style={{ animation: 'fade-in 0.35s ease' }}>
        <PageHeader title="Настройки" subtitle="Профиль и оформление" />
        <ProfileSection />
        {/* Security and Telegram are per-user, not company settings: every
            signed-in person manages their own passkeys, sessions and password
            regardless of role. */}
        <div style={{ marginTop: 16 }}>
          <SecuritySection />
        </div>
        <div style={{ marginTop: 16 }}>
          <TelegramSection />
        </div>
        <div style={{ marginTop: 24 }}>
          <Forbidden message="Системные настройки — пользователи, объекты, правила и интеграции — доступны только владельцу." />
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader title="Настройки" subtitle="Компания, доступ и интеграции" />
      <ProfileSection />
      <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
        <BackgroundSection />
        <SecuritySection />
        <UsersSection />
        <TelegramSection />
        <IntegrationsSection />
        <AdministrationSection />
      </div>
    </div>
  );
}
